import { createFileRoute } from '@tanstack/react-router';
import { BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnalysisSwitcher } from '@/components/AnalysisSwitcher';
import { CurrencySwitcher } from '@/components/CurrencySwitcher';
import {
  AmountChart,
  ChartCard,
  ChartLegend,
  CostVsMarketChart,
  MarketOverlayChart,
  useChartRows,
} from '@/components/charts/ChartCard';
import { AppShell } from '@/components/layout/AppShell';
import { Simulator } from '@/components/Simulator';
import { useTransactionComposer } from '@/components/TransactionComposer';
import { Card, EmptyState, Skeleton, Stat } from '@/components/ui/primitives';
import { formatAmount, formatRate } from '@/lib/format';
import { useRateHistory, useSeries } from '@/lib/queries';
import { useChartTokens } from '@/lib/theme';
import { useSelectedCurrency } from '@/lib/useSelectedCurrency';

export const Route = createFileRoute('/app/stats')({ component: Stats });

function Stats() {
  const { t, i18n } = useTranslation();
  const c = useChartTokens();
  const { openComposer } = useTransactionComposer();

  const { quote, select, available, positions, isLoading } = useSelectedCurrency();
  const position = positions.find((p) => p.quoteCurrency === quote);

  const { data: points, isLoading: seriesLoading } = useSeries(quote ?? undefined);

  const rows = useChartRows(points, i18n.language);

  // The window follows the ledger, so the overlay covers exactly the period the
  // user actually has records for - no leading flat run before the first trade.
  const { data: history } = useRateHistory(
    position?.baseCurrency,
    quote ?? undefined,
    rows[0]?.occurredOn,
    rows[rows.length - 1]?.occurredOn,
  );

  return (
    <AppShell
      title={t('nav.analysis')}
      toolbar={<CurrencySwitcher available={available} value={quote} onChange={select} />}
      subnavigation={<AnalysisSwitcher />}
      {...(quote ? { addDefaults: { quoteCurrency: quote } } : {})}
    >
      <div className="flex flex-col gap-4">
        <div className="lg:hidden">
          <CurrencySwitcher available={available} value={quote} onChange={select} />
        </div>

        {isLoading || seriesLoading ? (
          <>
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-72 w-full" />
          </>
        ) : !position || rows.length === 0 ? (
          <EmptyState
            icon={<BarChart3 size={40} strokeWidth={1.5} />}
            title={t('ledger.empty')}
            body={t('ledger.emptyHint')}
          />
        ) : (
          <>
            <Card>
              <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-4">
                <Stat
                  label={t('position.holding')}
                  value={formatAmount(position.netQuote, position.quoteCurrency)}
                  sub={position.quoteCurrency}
                />
                <Stat
                  label={t('position.averageRate')}
                  value={position.averageRate === null ? '—' : formatRate(position.averageRate)}
                />
                <Stat
                  label={t('position.cost')}
                  value={formatAmount(position.netBase, position.baseCurrency)}
                  sub={position.baseCurrency}
                />
                <Stat
                  label={t('position.realized')}
                  value={formatAmount(position.realizedPnl, position.baseCurrency)}
                  tone={
                    Number(position.realizedPnl) > 0
                      ? 'positive'
                      : Number(position.realizedPnl) < 0
                        ? 'negative'
                        : 'neutral'
                  }
                  sub={position.baseCurrency}
                />
              </div>
            </Card>

            <ChartCard
              title={t('stats.costVsMarket')}
              hint={`${position.baseCurrency} / ${position.quoteCurrency}`}
              height={280}
            >
              <CostVsMarketChart rows={rows} quoteCurrency={position.quoteCurrency} />
            </ChartCard>
            <ChartLegend
              items={[
                { color: c.average, label: t('stats.yourAverage') },
                { color: c.buy, label: t('ledger.buy') },
                { color: c.sell, label: t('ledger.sell') },
              ]}
            />

            {(history?.length ?? 0) > 1 && (
              <>
                <ChartCard title={t('stats.rateHistory')} height={260}>
                  <MarketOverlayChart
                    rows={rows}
                    history={history!}
                    quoteCurrency={position.quoteCurrency}
                  />
                </ChartCard>
                <ChartLegend
                  items={[
                    { color: c.average, label: t('stats.yourAverage') },
                    { color: c.market, label: t('stats.marketRate') },
                  ]}
                />
              </>
            )}

            <ChartCard title={t('stats.amounts')} height={220}>
              <AmountChart rows={rows} quoteCurrency={position.quoteCurrency} />
            </ChartCard>

            <Simulator
              position={position}
              onRecord={(draft) =>
                openComposer({ ...draft, ...(quote ? { quoteCurrency: quote } : {}) })
              }
            />
          </>
        )}
      </div>
    </AppShell>
  );
}
